/**
 * WorkflowDesignerPage - Complete visual workflow designer
 * 
 * Integrates:
 * - AgentPalette (left sidebar)
 * - WorkflowCanvas (center canvas)
 * - FieldMapper (configuration panel)
 * - BusinessRulesBuilder (rules panel)
 * 
 * For SaaS clients to visually design workflows
 */

import React, { useState, useCallback, useEffect } from 'react';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { AgentPalette } from '../palette/AgentPalette';
import { WorkflowCanvas } from '../canvas/WorkflowCanvas';
import { FieldMapper } from '../field-mapper/FieldMapper';
import { BusinessRulesBuilder } from '../rules/BusinessRulesBuilder';
import { listWorkflowAgents, WorkflowAgent } from '../../../api/agents';
import NLWorkflowInput from '../nl-builder/NLWorkflowInput';
import NLWorkflowPreview from '../nl-builder/NLWorkflowPreview';
import WorkflowTemplateLibrary from '../nl-builder/WorkflowTemplateLibrary';
import type { ParsedWorkflow, ParsedStep, WorkflowTemplate } from '../nl-builder/types';

interface WorkflowDesignerPageProps {
  initialWorkflow?: any;
  onSave?: (workflow: any) => void;
  onExecute?: (workflow: any) => void;
}

export const WorkflowDesignerPage: React.FC<WorkflowDesignerPageProps> = ({
  initialWorkflow,
  onSave,
  onExecute
}) => {
  // Workflow state
  const [workflow, setWorkflow] = useState(initialWorkflow || {
    name: 'New Workflow',
    description: '',
    steps: [],
    fieldMappings: [],
    businessRules: []
  });

  // UI state
  const [, setSelectedStep] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'none' | 'mapping' | 'rules'>('none');
  const [isSaving, setIsSaving] = useState(false);

  // Agent data state
  const [agents, setAgents] = useState<WorkflowAgent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState<string | null>(null);

  // NL Builder state (T7.2)
  const [designerTab, setDesignerTab] = useState<'canvas' | 'ai-builder'>('canvas');
  const [parsedWorkflow, setParsedWorkflow] = useState<ParsedWorkflow | null>(null);
  const [parseConfidence, setParseConfidence] = useState(0);
  const [showTemplates, setShowTemplates] = useState(false);

  // Load agents from API on mount
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setAgentsLoading(true);
        setAgentsError(null);

        console.log('[WorkflowDesigner] Fetching agents from API');
        const response = await listWorkflowAgents();

        if (response.success && response.data.Records) {
          setAgents(response.data.Records);
          console.log('[WorkflowDesigner] Loaded agents:', response.data.Records.length);
        } else {
          throw new Error('Failed to load workflow agents: Invalid response format');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load workflow agents';
        console.error('[WorkflowDesigner] Error loading agents:', err);
        setAgentsError(errorMessage);
        setAgents([]); // Empty state, not mock data - fail loud
      } finally {
        setAgentsLoading(false);
      }
    };

    fetchAgents();
  }, []);

  // Field definitions for mapping
  const [sourceFields] = useState([
    { name: 'employee_id', type: 'string' as const, required: true },
    { name: 'first_name', type: 'string' as const, required: true },
    { name: 'last_name', type: 'string' as const, required: true },
    { name: 'hours_worked', type: 'number' as const, required: true },
    { name: 'hourly_rate', type: 'number' as const },
    { name: 'project_code', type: 'string' as const }
  ]);

  const [targetFields] = useState([
    { name: 'worker_id', type: 'string' as const, required: true },
    { name: 'name_full', type: 'string' as const, required: true },
    { name: 'total_hours', type: 'number' as const, required: true },
    { name: 'cost', type: 'number' as const },
    { name: 'project', type: 'string' as const }
  ]);

  // Available fields for business rules
  const availableFields = [
    'total_hours',
    'cost',
    'project',
    'employee_count',
    'transaction_amount'
  ];

  // Handle drag from palette to canvas
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    // Check if dropped over the canvas
    if (over && over.id === 'workflow-canvas' && active.data.current?.agent) {
      const agent = active.data.current.agent;

      // Create new workflow step
      const newStep = {
        id: `step-${Date.now()}`,
        agentId: agent.id,
        agentName: agent.name,
        position: { x: 100 + workflow.steps.length * 150, y: 100 },
        configuration: {},
        connections: []
      };

      // Add to workflow
      setWorkflow((prev: typeof workflow) => ({
        ...prev,
        steps: [...prev.steps, newStep]
      }));

      console.log('[WorkflowDesigner] Added agent to canvas:', agent.name);
    }
  }, [workflow.steps.length]);

  // Handle workflow changes
  const handleWorkflowChange = useCallback((updatedWorkflow: typeof workflow) => {
    setWorkflow(updatedWorkflow);
  }, []);

  // Handle step selection
  const handleStepSelect = useCallback((stepId: string) => {
    setSelectedStep(stepId);
    setActivePanel('mapping'); // Show mapping panel when step selected
  }, []);

  // Handle field mapping changes
  const handleMappingCreate = useCallback((mapping: { sourceField: string; targetField: string }) => {
    setWorkflow((prev: typeof workflow) => ({
      ...prev,
      fieldMappings: [...prev.fieldMappings, mapping]
    }));
  }, []);

  const handleMappingUpdate = useCallback((mapping: { sourceField: string; targetField: string }) => {
    setWorkflow((prev: typeof workflow) => ({
      ...prev,
      fieldMappings: prev.fieldMappings.map((m: { sourceField: string; targetField: string }) =>
        m.sourceField === mapping.sourceField && m.targetField === mapping.targetField
          ? mapping
          : m
      )
    }));
  }, []);

  const handleMappingDelete = useCallback((sourceField: string, targetField: string) => {
    setWorkflow((prev: typeof workflow) => ({
      ...prev,
      fieldMappings: prev.fieldMappings.filter((m: { sourceField: string; targetField: string }) =>
        !(m.sourceField === sourceField && m.targetField === targetField)
      )
    }));
  }, []);

  // Handle business rule changes
  const handleRuleAdd = useCallback((rule: { id: string }) => {
    setWorkflow((prev: typeof workflow) => ({
      ...prev,
      businessRules: [...prev.businessRules, rule]
    }));
  }, []);

  const handleRuleUpdate = useCallback((rule: { id: string }) => {
    setWorkflow((prev: typeof workflow) => ({
      ...prev,
      businessRules: prev.businessRules.map((r: { id: string }) =>
        r.id === rule.id ? rule : r
      )
    }));
  }, []);

  const handleRuleDelete = useCallback((ruleId: string) => {
    setWorkflow((prev: typeof workflow) => ({
      ...prev,
      businessRules: prev.businessRules.filter((r: { id: string }) => r.id !== ruleId)
    }));
  }, []);

  // NL Builder handlers (T7.2)
  const handleNLWorkflowGenerated = useCallback((wf: ParsedWorkflow, confidence: number) => {
    setParsedWorkflow(wf);
    setParseConfidence(confidence);
  }, []);

  const handleNLConfirm = useCallback((steps: ParsedStep[]) => {
    // Merge generated steps into workflow canvas
    const newSteps = steps.map((step, idx) => ({
      id: `step-${Date.now()}-${idx}`,
      agentId: step.matchedAgentId || '',
      agentName: step.matchedAgentName || step.description,
      position: { x: 100 + (workflow.steps.length + idx) * 150, y: 100 + idx * 80 },
      configuration: step.configuration || {},
      connections: idx < steps.length - 1 ? [`step-${Date.now()}-${idx + 1}`] : [],
    }));

    setWorkflow((prev: typeof workflow) => ({
      ...prev,
      name: parsedWorkflow?.name || prev.name,
      description: parsedWorkflow?.description || prev.description,
      steps: [...prev.steps, ...newSteps],
    }));

    setParsedWorkflow(null);
    setDesignerTab('canvas');
  }, [workflow.steps.length, parsedWorkflow]);

  const handleTemplateSelect = useCallback((template: WorkflowTemplate) => {
    setParsedWorkflow({
      name: template.name,
      description: template.description,
      steps: template.steps,
      estimatedComplexity: template.steps.length <= 3 ? 'low' : template.steps.length <= 6 ? 'medium' : 'high',
    });
    setParseConfidence(0.85);
    setShowTemplates(false);
    setDesignerTab('ai-builder');
  }, []);

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (onSave) {
        await onSave(workflow);
      }
      console.log('Workflow saved:', workflow);
      alert('Workflow saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save workflow');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle execute
  const handleExecute = async () => {
    try {
      if (onExecute) {
        await onExecute(workflow);
      }
      console.log('Workflow executed:', workflow);
      alert('Workflow execution started!');
    } catch (error) {
      console.error('Execute error:', error);
      alert('Failed to execute workflow');
    }
  };

  // Calculate workflow complexity
  const calculateComplexity = () => {
    const stepCount = workflow.steps.length;
    const mappingCount = workflow.fieldMappings.length;
    const ruleCount = workflow.businessRules.length;

    const score = (stepCount * 10) + (mappingCount * 5) + (ruleCount * 8);

    if (score < 30) return { level: 'Low', color: '#10b981', score };
    if (score < 60) return { level: 'Medium', color: '#f59e0b', score };
    return { level: 'High', color: '#ef4444', score };
  };

  const complexity = calculateComplexity();

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div className="workflow-designer-page" style={{ display: 'flex', height: '100vh', background: '#f5f5f5' }}>

        {/* Left Sidebar - Agent Palette */}
        <div style={{ width: '300px', background: 'white', borderRight: '1px solid #ddd', overflowY: 'auto' }}>
          {agentsLoading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Loading workflow agents...
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                Connecting to agent registry
              </div>
            </div>
          ) : agentsError ? (
            <div style={{ padding: '20px' }}>
              <div style={{
                background: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '4px',
                padding: '16px',
                marginBottom: '12px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#991b1b',
                  marginBottom: '8px'
                }}>
                  Error Loading Agents
                </div>
                <div style={{ fontSize: '13px', color: '#7f1d1d', marginBottom: '8px' }}>
                  {agentsError}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#7f1d1d',
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid #fca5a5'
                }}>
                  <strong>Required:</strong> Backend endpoint /api/agents/workflow-palette must be deployed
                </div>
              </div>
              <button
                onClick={() => window.location.reload()}
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Retry
              </button>
            </div>
          ) : agents.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                No workflow agents available
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                Agent registry returned empty list
              </div>
            </div>
          ) : (
            <AgentPalette
              agents={agents}
              onAgentSelect={(agent: any) => console.log('Agent selected:', agent)}
            />
          )}
        </div>

        {/* Center - Workflow Canvas */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ 
            padding: '16px', 
            background: 'white', 
            borderBottom: '1px solid #ddd',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px' }}>
                <input
                  type="text"
                  value={workflow.name}
                  onChange={(e) => setWorkflow({ ...workflow, name: e.target.value })}
                  style={{ 
                    border: 'none', 
                    fontSize: '20px', 
                    fontWeight: 'bold',
                    background: 'transparent',
                    width: '300px'
                  }}
                  placeholder="Workflow Name"
                />
              </h2>
              <input
                type="text"
                value={workflow.description}
                onChange={(e) => setWorkflow({ ...workflow, description: e.target.value })}
                style={{ 
                  border: 'none', 
                  fontSize: '14px',
                  color: '#666',
                  background: 'transparent',
                  width: '400px',
                  marginTop: '4px'
                }}
                placeholder="Workflow Description"
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Designer Tab Toggle (T7.2) */}
              <div style={{ display: 'flex', gap: '2px', background: '#f3f4f6', borderRadius: '6px', padding: '2px' }}>
                <button
                  onClick={() => setDesignerTab('canvas')}
                  style={{
                    padding: '6px 14px',
                    background: designerTab === 'canvas' ? 'white' : 'transparent',
                    color: designerTab === 'canvas' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: designerTab === 'canvas' ? '600' : '400',
                    boxShadow: designerTab === 'canvas' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  }}
                >
                  Canvas
                </button>
                <button
                  onClick={() => setDesignerTab('ai-builder')}
                  style={{
                    padding: '6px 14px',
                    background: designerTab === 'ai-builder' ? 'white' : 'transparent',
                    color: designerTab === 'ai-builder' ? '#111827' : '#6b7280',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: designerTab === 'ai-builder' ? '600' : '400',
                    boxShadow: designerTab === 'ai-builder' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  }}
                >
                  AI Builder
                </button>
              </div>

              {/* Templates button */}
              <button
                onClick={() => setShowTemplates(true)}
                style={{
                  padding: '8px 16px',
                  background: '#e0e7ff',
                  color: '#4338ca',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                Templates
              </button>

              {/* Complexity Indicator */}
              <div style={{
                padding: '8px 16px',
                background: complexity.color,
                color: 'white',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Complexity: {complexity.level} ({complexity.score})
              </div>

              <button
                onClick={() => setActivePanel(activePanel === 'mapping' ? 'none' : 'mapping')}
                style={{
                  padding: '8px 16px',
                  background: activePanel === 'mapping' ? '#3b82f6' : '#e5e7eb',
                  color: activePanel === 'mapping' ? 'white' : '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Field Mapping
              </button>

              <button
                onClick={() => setActivePanel(activePanel === 'rules' ? 'none' : 'rules')}
                style={{
                  padding: '8px 16px',
                  background: activePanel === 'rules' ? '#3b82f6' : '#e5e7eb',
                  color: activePanel === 'rules' ? 'white' : '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Business Rules ({workflow.businessRules.length})
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  background: isSaving ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSaving ? 'not-allowed' : 'pointer'
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>

              <button
                onClick={handleExecute}
                style={{
                  padding: '8px 16px',
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Execute
              </button>
            </div>
          </div>

          {/* Canvas / AI Builder */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            {designerTab === 'canvas' ? (
              <WorkflowCanvas
                workflow={workflow}
                onChange={handleWorkflowChange}
                onStepSelect={handleStepSelect}
              />
            ) : (
              <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                {parsedWorkflow ? (
                  <NLWorkflowPreview
                    workflow={parsedWorkflow}
                    confidence={parseConfidence}
                    availableAgents={agents.map(a => ({
                      id: a.id,
                      name: a.name,
                      category: a.category,
                    }))}
                    onConfirm={handleNLConfirm}
                    onCancel={() => setParsedWorkflow(null)}
                  />
                ) : (
                  <NLWorkflowInput
                    availableAgents={agents.map(a => ({
                      id: a.id,
                      name: a.name,
                      category: a.category,
                      description: a.description,
                    }))}
                    onWorkflowGenerated={handleNLWorkflowGenerated}
                  />
                )}
              </div>
            )}
          </div>

          {/* Workflow Summary */}
          <div style={{ 
            padding: '12px 16px', 
            background: 'white', 
            borderTop: '1px solid #ddd',
            fontSize: '14px',
            color: '#666'
          }}>
            {workflow.steps.length} steps · {workflow.fieldMappings.length} field mappings · {workflow.businessRules.length} business rules
          </div>
        </div>

        {/* Right Sidebar - Configuration Panels */}
        {activePanel !== 'none' && (
          <div style={{ 
            width: '400px', 
            background: 'white', 
            borderLeft: '1px solid #ddd',
            overflowY: 'auto'
          }}>
            {activePanel === 'mapping' && (
              <div style={{ padding: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{ margin: 0 }}>Field Mapping</h3>
                  <button
                    onClick={() => setActivePanel('none')}
                    style={{
                      padding: '4px 8px',
                      background: '#e5e7eb',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
                <FieldMapper
                  sourceFields={sourceFields}
                  targetFields={targetFields}
                  mappings={workflow.fieldMappings}
                  onMappingCreate={handleMappingCreate}
                  onMappingUpdate={handleMappingUpdate}
                  onMappingDelete={handleMappingDelete}
                />
              </div>
            )}

            {activePanel === 'rules' && (
              <div style={{ padding: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{ margin: 0 }}>Business Rules</h3>
                  <button
                    onClick={() => setActivePanel('none')}
                    style={{
                      padding: '4px 8px',
                      background: '#e5e7eb',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
                <BusinessRulesBuilder
                  rules={workflow.businessRules}
                  availableFields={availableFields}
                  onRuleAdd={handleRuleAdd}
                  onRuleUpdate={handleRuleUpdate}
                  onRuleDelete={handleRuleDelete}
                />
              </div>
            )}
          </div>
        )}

      </div>

      {/* Template Library Modal (T7.2) */}
      {showTemplates && (
        <WorkflowTemplateLibrary
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplates(false)}
        />
      )}
    </DndContext>
  );
};
